import {
    ExpandLess,
    ExpandMore,
    UnfoldLess,
    UnfoldMore
} from '@mui/icons-material';
import {
    Box,
    Button,
    Grid,
    IconButton,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { ApplicationNameAdminAppModalCell } from 'application/Shared/Components/ApplicationNameAdminAppModalCell';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { HMS } from 'application/Shared/Components/Usage';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, hmsFormatter } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  Objects  */
import { Application } from 'objects/Application';
import { User, UserFullName, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Sparklines, SparklinesBars, SparklinesLine } from 'react-sparklines';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

/*  Components  */
import CoverPage from '../Components/Containers/CoverPage';

/*  Constants  */
const { isset } = Helpers;

/*  Styles  */
const sxs = {
    text: {
        small: { fontSize: FontSizes[0] }
    }
};

const UsageReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup, runReport } =
        props;
    if (typeof reportData === 'undefined') {
        return null;
    }

    return (
        <Box data-trackid="usageReportGridContainer" width="100%">
            {reportSetup.reportSelections?.organizationalUsageCover && (
                <CoverPage {...props} />
            )}

            {/* organizational */}

            {reportData.organizationalUsageOverview && (
                <Box data-trackid="usageReportOrganizationalUsageOverviewWrapper">
                    <UsageOverview
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.organizationalUsageOverview}
                    />
                </Box>
            )}
            {reportData.organizationalUsageOverviewBreakdown && (
                <Box data-trackid="usageReportOrganizationalUsageOverviewBreakdownWrapper">
                    <UsageBreakdown
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={
                            reportData.organizationalUsageOverviewBreakdown
                        }
                    />
                </Box>
            )}
            {reportData.organizationalUsageDailyBreakdown && (
                <Box
                    pl="16px"
                    pr="16px"
                    width="100%"
                    data-trackid="usageReportOrganizationalUsageDailyBreakdownWrapper"
                    className="break-before"
                >
                    <UsageDailyBreakdown
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={
                            reportData.organizationalUsageDailyBreakdown
                        }
                        runReport={runReport}
                    />
                </Box>
            )}
            {reportData.organizationalUsageVsAdoption && (
                <Box data-trackid="usageReportOrganizationalUsageVsAdoptionWrapper">
                    <UsageVsAdoption
                        {...props}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.organizationalUsageVsAdoption}
                    />
                </Box>
            )}
        </Box>
    );
};

const UsageOverview = (props) => {
    const theme = useTheme();
    const { renderingScheduledReport, reportData, reportWrapper, t } = props;

    const usageReportKeys = [];
    if (reportData) {
        reportData.forEach((report) => {
            Object.keys(report).forEach((key) => {
                if (key !== 'name' && !usageReportKeys.includes(key)) {
                    usageReportKeys.push(key);
                }
            });
        });
    }

    if (renderingScheduledReport) {
        return (
            <Box>
                {!reportWrapper && (
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_overview')}
                    </Typography>
                )}
                <ResponsiveContainer height={300} width="100%">
                    <BarChart
                        width={700}
                        height={240}
                        data={reportData}
                        margin={{
                            bottom: 5,
                            left: 20,
                            right: 30,
                            top: 20
                        }}
                    >
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={hmsFormatter} />
                        <Tooltip />
                        <Legend />
                        {usageReportKeys.map((row, index) => (
                            <Bar
                                isAnimationActive={false}
                                key={`iuo_${row}`}
                                dataKey={row}
                                stackId="a"
                                fill={
                                    theme.palette.graph.colors[
                                        index %
                                            theme.palette.graph.colors.length
                                    ]
                                }
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    return (
        <Box
            margin={
                !isset(props, 'reportWrapperProps', 'height') ? '32px' : '0px'
            }
        >
            {!reportWrapper && (
                <>
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_overview')}
                    </Typography>
                    <hr />
                </>
            )}
            <ResponsiveContainer height={300}>
                <BarChart
                    data={reportData}
                    margin={{
                        bottom: isset(props, 'reportWrapperProps', 'noLegend')
                            ? -5
                            : 5,
                        left: 35,
                        right: 20,
                        top: 10
                    }}
                >
                    <CartesianGrid
                        stroke={theme.palette.chart.axis}
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={hmsFormatter} />
                    <Tooltip formatter={hmsFormatter} />
                    {!isset(props, 'reportWrapperProps', 'noLegend') && (
                        <Legend
                            iconSize={10}
                            layout="horizontal"
                            align="center"
                            wrapperStyle={{
                                fontSize: FontSizes[0],
                                top: '275px'
                            }}
                        />
                    )}
                    {usageReportKeys.map((row, index) => (
                        <Bar
                            key={`iuo_${row}`}
                            dataKey={row}
                            stackId="a"
                            fill={
                                theme.palette.graph.colors[
                                    index % theme.palette.graph.colors.length
                                ]
                            }
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

const RenderRowUsageBreakdown = ({ peakUsage, row }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Application
            key={`iub_app_${row.app.id}_reportsUsageBreakdown}`}
            data={row.app}
            fromPage={t('Reports.Title.organizational.report')}
            nameHasLink
        >
            <TableCell>
                <ApplicationNameLinkCell withCategory categoryNoTruncate />
            </TableCell>
            <TableCell align="right">
                <HMS seconds={row.usage} />
            </TableCell>
            <TableCell align="right">{row.percent}%</TableCell>
            <TableCell align="right">
                {row.link && row.link.usageChart && (
                    <div style={{ width: 150 }}>
                        <Sparklines
                            data={Helpers.padArray(row.link.usageChart, 20, 0)}
                            max={peakUsage}
                        >
                            <SparklinesBars
                                style={{
                                    fill: theme.palette.sparkLines.sparkBar,
                                    stroke: theme.palette.sparkLines.stroke
                                }}
                            />
                            <SparklinesLine
                                color={theme.palette.sparkLines.sparkLine}
                                style={{
                                    fill: 'none'
                                }}
                            />
                        </Sparklines>
                    </div>
                )}
            </TableCell>
        </Application>
    );
};

const headersUsageBreakdown = (t) => [
    {
        align: 'left',
        field: 'riubApplication',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '60%'
    },
    {
        align: 'right',
        field: 'riubUsage',
        renderHeader: <strong>{t('Common.usage')}</strong>,
        width: '10%'
    },
    {
        align: 'right',
        field: 'riubPercentage',
        renderHeader: <strong>{t('Common.percentage')}</strong>,
        width: '10%'
    },
    {
        align: 'right',
        field: 'riubUsageChart',
        renderHeader: <strong>{t('Common.usage_chart')}</strong>,
        width: '20%'
    }
];

const UsageBreakdown = ({
    renderingScheduledReport,
    reportData,
    reportWrapper,
    t
}) => {
    let peakUsage = 0;

    reportData.forEach((report) => {
        report?.link?.usageChart?.forEach((usage) => {
            if (peakUsage < parseInt(usage, 10)) {
                peakUsage = parseInt(usage, 10);
            }
        });
    });

    const csvExportFields = [
        {
            key: 'app.name',
            label: 'Application Name'
        },
        {
            key: 'app.category',
            label: 'Application Category'
        },
        {
            key: 'app.subCategory',
            label: 'Application Sub-Category'
        },
        {
            key: 'usage',
            label: 'Usage',
            transform: 'hms'
        },
        {
            key: 'percent',
            label: 'Percentage'
        }
    ];

    const rowsPerPage = renderingScheduledReport ? -1 : 10;

    const tableHeadersUsageBreakdown = useMemo(
        () => headersUsageBreakdown(t),
        [t]
    );

    return (
        <Box
            margin={!reportWrapper ? '30px' : '0px'}
            marginTop={!reportWrapper ? '50px' : '0px'}
        >
            {!reportWrapper && (
                <>
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_breakdown')} |{' '}
                        {t('Reports.UsageReport.top10_apps')}
                    </Typography>
                    <hr />
                </>
            )}
            <ManagedTable
                csvExport
                csvExportFields={csvExportFields}
                csvExportFileName="usage_top_applications"
                headers={tableHeadersUsageBreakdown}
                renderBody={(row) => (
                    <RenderRowUsageBreakdown peakUsage={peakUsage} row={row} />
                )}
                renderBodyKey={null}
                renderFooter
                rows={reportData}
                rowsPerPage={rowsPerPage}
                searchEnabled={false}
                sortDirection="DESC"
                sortFieldType="number"
                sortKey="usage"
            />
        </Box>
    );
};

class UsageDailyBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            csvData: [],
            csvHeaders: [],
            data: [],
            headers: [],
            loaded: false,
            showCollapse: false,
            showExpand: true
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        const { reportData } = this.props;

        this.loadHeaders();

        if (Helpers.isEmpty(reportData)) {
            debug.w('No Report Data Found', reportData);
            return;
        }

        const { data, uniqueDates } = reportData;
        if (!Array.isArray(data)) {
            return;
        }

        this.loadData({ data, uniqueDates });
    };

    loadHeaders = async () => {
        const { t, reportSetup, renderingScheduledReport, reportData } =
            this.props;
        const { period } = reportSetup;
        const { uniqueDates } = reportData;

        const sx = sxs.text.small;

        const headers = [];
        const csvHeaders = [
            { key: 'name', label: 'App Name / User Name' },
            { key: 'email', label: 'User Email' },
            { key: 'appName', label: 'App Name' },
            { key: 'usage', label: 'Usage' },
            { key: 'date', label: 'Date' }
        ];

        if (period === 'weekly') {
            if (!renderingScheduledReport) {
                headers.push({
                    align: 'left',
                    field: 'chevron',
                    renderHeader: <strong>&nbsp;</strong>,
                    width: '1%'
                });
            }
            headers.push(
                {
                    align: 'left',
                    field: 'application',
                    renderHeader: <strong>{t('Common.application')}</strong>,
                    width: '30%'
                },
                {
                    align: 'right',
                    field: 'sun',
                    renderHeader: <strong>{t('Common.days_sun')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'mon',
                    renderHeader: <strong>{t('Common.days_mon')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'tues',
                    renderHeader: <strong>{t('Common.days_tue')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'wednes',
                    renderHeader: <strong>{t('Common.days_wed')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'thurs',
                    renderHeader: <strong>{t('Common.days_thu')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'fri',
                    renderHeader: <strong>{t('Common.days_fri')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'saturday',
                    renderHeader: <strong>{t('Common.days_sat')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'total',
                    renderHeader: <strong>{t('Common.total')}</strong>,
                    width: '10%'
                }
            );
            this.setState({ csvHeaders, headers });
            return;
        }

        headers.push(
            {
                align: 'left',
                field: 'chevron',
                renderHeader: <strong style={sx}>&nbsp;</strong>,
                squish: true,
                width: '40px'
            },
            {
                align: 'left',
                field: 'application',
                renderHeader: (
                    <strong style={sx}>{t('Common.application')}</strong>
                ),
                width: '300px'
            }
        );

        Object.values(uniqueDates).forEach((d) => {
            const { date, position } = d;

            headers.push({
                align: 'right',
                field: `iudb_header_${position}`,
                renderHeader: <strong style={sx}>{date}</strong>,
                squish: true,
                width: '80px'
            });
        });

        headers.push({
            align: 'right',
            field: 'total',
            renderHeader: <strong style={sx}>Total</strong>,
            squish: true,
            width: '80px'
        });

        this.setState({ csvHeaders, headers });
    };

    loadData = (p) => {
        const { uniqueDates } = p;
        const today = new Date();

        const csvData = [];
        const data = p.data.map((a) => {
            const { application, userBreakdown } = a;

            const appName = application.name;
            Object.values(uniqueDates).forEach((d) => {
                const { date, position } = d;
                const dateObj = new Date(date);
                if (dateObj > today) {
                    return;
                }
                const usage = Helpers.isset(a, 'days', position)
                    ? a.days[position]
                    : 0;
                csvData.push({
                    ...a,
                    appName,
                    date,
                    email: '',
                    name: appName,
                    usage
                });
            });

            if (!userBreakdown) {
                return { ...a, expanded: false };
            }

            userBreakdown.forEach((u) => {
                const { user } = u;

                let email = '';
                if (user) {
                    email = user.email;
                }

                Object.values(uniqueDates).forEach((d) => {
                    const { date, position } = d;
                    const dateObj = new Date(date);
                    if (dateObj > today) {
                        return;
                    }

                    const usage = Helpers.isset(u, 'days', position)
                        ? u.days[position]
                        : 0;
                    csvData.push({
                        ...user,
                        appName,
                        date,
                        email,
                        usage
                    });
                });
            });

            return { ...a, expanded: false };
        });

        this.setState({ csvData, data, loaded: true });
    };

    handleAll = (expanded) => {
        let { data } = this.state;
        data = data.map((d) => ({ ...d, expanded }));
        this.setState({ data, showCollapse: expanded, showExpand: !expanded });
    };

    handleExpand = (row) => {
        const { total } = this.state;
        let { data } = this.state;

        let expandedCount = 0;
        let collapsedCount = 0;

        data = data.map((d) => {
            const { application } = d;
            let { expanded } = d;
            if (application.id !== row.application.id) {
                if (expanded) {
                    expandedCount += 1;
                } else {
                    collapsedCount += 1;
                }
                return d;
            }

            expanded = !expanded;
            if (expanded) {
                expandedCount += 1;
            } else {
                collapsedCount += 1;
            }

            return { ...d, expanded };
        });

        let showExpand = false;
        let showCollapse = false;
        if (expandedCount > 0) {
            if (expandedCount !== total) {
                showCollapse = true;
            }
        } else {
            showExpand = true;
        }

        if (collapsedCount > 0) {
            if (collapsedCount !== total) {
                showExpand = true;
            }
        } else {
            showCollapse = true;
        }

        this.setState({ data, showCollapse, showExpand });
    };

    renderButtons = () => {
        const { t } = this.props;
        const { showExpand, showCollapse } = this.state;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '8px',
                    marginTop: '8px',
                    width: '100%'
                }}
            >
                {showCollapse && (
                    <Button
                        variant="outlined"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(false)}
                    >
                        <UnfoldLess fontSize="small" />
                        {t('Reports.collapse_all')}
                    </Button>
                )}
                {showExpand && (
                    <Button
                        variant="contained"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(true)}
                        style={{ marginLeft: '8px' }}
                    >
                        <UnfoldMore fontSize="small" />
                        {t('Reports.expand_all')}
                    </Button>
                )}
            </Box>
        );
    };

    renderRow = (row) => {
        const { t, renderingScheduledReport, runReport } = this.props;
        const { data } = this.state;

        return (
            <Application
                data={row.application}
                key={`usage_app__${row.application.id}`}
            >
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    {!renderingScheduledReport && (
                        <TableCell>
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => this.handleExpand(row)}
                                sx={{ color: 'icon.textColor' }}
                            >
                                {row.expanded ? (
                                    <ExpandLess fontSize="small" />
                                ) : (
                                    <ExpandMore fontSize="small" />
                                )}
                            </IconButton>
                        </TableCell>
                    )}
                    <TableCell>
                        <ApplicationNameAdminAppModalCell
                            onCloseModal={runReport}
                            row={row}
                            nameNoTruncate
                        />
                    </TableCell>
                    {row.days.map((usage) => (
                        <TableCell key={`usage_${Math.random()}`} align="right">
                            <HMS seconds={usage} sx={sxs.text.small} />
                            {(row.expanded || renderingScheduledReport) && (
                                <>
                                    <br />
                                    <small>
                                        <HMS
                                            seconds={
                                                row?.userBreakdown?.length > 0
                                                    ? Math.round(
                                                          usage /
                                                              row.userBreakdown
                                                                  .length
                                                      )
                                                    : 0
                                            }
                                            sx={sxs.text.small}
                                        />
                                    </small>
                                </>
                            )}
                        </TableCell>
                    ))}
                    <TableCell align="right">
                        <HMS seconds={row.usage} sx={sxs.text.small} />
                        {(row.expanded || renderingScheduledReport) && (
                            <>
                                <br />
                                <small>
                                    <HMS
                                        seconds={Math.round(
                                            row.usage / row.userBreakdown.length
                                        )}
                                        sx={sxs.text.small}
                                    />
                                </small>
                            </>
                        )}
                    </TableCell>
                </TableRow>
                {(row.expanded || data.length === 1) && (
                    <TableRow
                        sx={{
                            backgroundColor: 'table.subtableBackground'
                        }}
                    >
                        <TableCell
                            colSpan={100}
                            sx={{
                                backgroundColor: 'table.subtableHeader'
                            }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                width="100%"
                            >
                                <ApplicationNameLinkCell />
                                <Box ml="4px">{t('Common.breakdown')}</Box>
                            </Box>
                            <Box mt="8px">
                                <strong
                                    style={{
                                        width: '140px'
                                    }}
                                >
                                    {t('Reports.UsageReport.application_users')}
                                    :{' '}
                                </strong>{' '}
                                {row.userBreakdown.length}
                                <br />
                                <strong style={{ width: '140px' }}>
                                    {t('Reports.UsageReport.average_usage')}:{' '}
                                </strong>{' '}
                                <HMS
                                    seconds={
                                        row?.userBreakdown?.length > 0
                                            ? Math.round(
                                                  row.usage /
                                                      row.userBreakdown.length
                                              )
                                            : 0
                                    }
                                />
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                {(row.expanded || data.length === 1) &&
                    row.userBreakdown
                        .sort((a, b) => b.usage - a.usage)
                        .map((user, index) => (
                            <TableRow
                                key={`ubreakdown_${row.applicationId}_u${user.user.id}`}
                                sx={{
                                    backgroundColor: 'table.subtableBackground'
                                }}
                            >
                                {!renderingScheduledReport && (
                                    <TableCell>
                                        <small>{index + 1}.</small>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <User data={user.user}>
                                        {!renderingScheduledReport && (
                                            <UserIconAndName />
                                        )}
                                        {renderingScheduledReport && (
                                            <UserFullName />
                                        )}
                                    </User>
                                </TableCell>
                                {user.days.map((userDailyUsage) => (
                                    <TableCell
                                        key={`ubreakdown_${
                                            row.applicationId
                                        }_u${user.user.id}_${Math.random()}`}
                                        align="right"
                                    >
                                        <HMS
                                            seconds={userDailyUsage}
                                            sx={sxs.text.small}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <HMS
                                        seconds={user.usage}
                                        sx={sxs.text.small}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
            </Application>
        );
    };

    render() {
        const { t, reportWrapper } = this.props;
        const { loaded, headers, csvHeaders, csvData, data } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Box className="notResponsive">
                {!reportWrapper && (
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_daily_breakdown')}
                    </Typography>
                )}
                {this.renderButtons()}
                <ManagedTable
                    csvData={csvData}
                    csvExport
                    csvExportFields={csvHeaders}
                    csvExportFileName="usage_breakdown_daily"
                    headers={headers}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey={null}
                    renderFooter
                    rows={data}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
        );
    }
}

const UsageVsAdoption = (props) => {
    const {
        renderingScheduledReport,
        reportData,
        reportWrapper,
        reportWrapperProps,
        t
    } = props;
    const theme = useTheme();

    const usageReportKeys = [];
    if (reportData) {
        reportData.forEach((report) => {
            Object.keys(report).forEach((key) => {
                if (key !== 'name' && !usageReportKeys.includes(key)) {
                    usageReportKeys.push(key);
                }
            });
        });
    }

    if (renderingScheduledReport) {
        return (
            <Box width="100%">
                {!reportWrapper && (
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_overview')}
                    </Typography>
                )}
                <ResponsiveContainer height={300} width="100%">
                    <BarChart
                        width={700}
                        height={240}
                        data={reportData}
                        margin={{
                            bottom: 5,
                            left: 20,
                            right: 30,
                            top: 20
                        }}
                    >
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={hmsFormatter} />
                        <Tooltip />
                        <Legend />
                        {usageReportKeys.map((row, index) => (
                            <Bar
                                isAnimationActive={false}
                                key={`iuo_${row}`}
                                dataKey={row}
                                stackId="a"
                                fill={
                                    theme.palette.graph.colors[
                                        index %
                                            theme.palette.graph.colors.length
                                    ]
                                }
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    // Plot that goes in the admin hub, applications dashboard, application overview tab
    return (
        <Grid
            item
            lg={reportWrapper ? 12 : 8}
            xs={12}
            style={{
                height: isset(props, 'reportWrapperProps', 'height')
                    ? reportWrapperProps.height
                    : '300px'
            }}
        >
            {!reportWrapper && (
                <Typography variant="h5">
                    {t('Reports.UsageReport.adoption_and_usage')}
                </Typography>
            )}
            <ResponsiveContainer height={300} width="100%">
                <ComposedChart
                    data={reportData}
                    margin={{ bottom: 0, left: 0, right: 35, top: 20 }}
                    width={800}
                    height={300}
                >
                    <XAxis
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        angle={-40}
                        dy={30}
                        height={70}
                        dx={-35}
                    />
                    <YAxis
                        yAxisId="left"
                        orientation="left"
                        tickCount={10}
                        tickFormatter={(value) => Math.ceil(value)}
                    />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={hmsFormatter}
                    />
                    <Tooltip
                        formatter={(value, name) => {
                            if (name === 'Average Usage Per User (Hours)') {
                                return [hmsFormatter(value), name];
                            }
                            return [value, name];
                        }}
                    />
                    <Legend />
                    <CartesianGrid
                        stroke={theme.palette.chart.axis}
                        strokeDasharray="3 3"
                    />
                    <Bar
                        name="Average Usage Per User (Hours)"
                        yAxisId="right"
                        dataKey="usage"
                        barSize={40}
                        fill={theme.palette.chart.bar}
                    />
                    <Line
                        name="Adoption (New Applications)"
                        yAxisId="left"
                        type="linear"
                        dataKey="adoption"
                        stroke={theme.palette.chart.line}
                        strokeWidth={3}
                        dot={{ strokeWidth: 5 }}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Grid>
    );
};

export default withTranslation()(UsageReports);
